import React from 'react';
import styled from '@emotion/styled';
import { X } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalContainer = styled.div`
  background: #232325;
  border-radius: 25px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    border-radius: 18px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 20px 20px 0 20px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 16px 0 16px;
    margin-bottom: 12px;
  }
`;

const ModalTitle = styled.h2`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9E9E9E;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.1);
    color: #F43F60;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalContent = styled.div`
  padding: 0 24px 24px 24px;
  
  @media (max-width: 768px) {
    padding: 0 20px 20px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 16px 16px 16px;
  }
`;

const ModalMessage = styled.p`
  color: #8E8E8E;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 24px 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 18px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Button = styled.button`
  font-family: 'Clash Grotesk';
  border: none;
  cursor: pointer;
  border-radius: 17.5px;
  font-size: 16px;
  font-weight: 500;
  padding: 12px 32px;
  transition: all 0.2s ease;
  min-width: 120px;
  
  @media (max-width: 480px) {
    min-width: 100%;
    padding: 14px 32px;
  }
`;

const CancelButton = styled(Button)`
  background: #292929;
  color: #8E8E8E;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: #363636;
    color: #EDEDED;
  }
`;

const ConfirmButton = styled(Button)`
  background: #F43F60;
  color: #FFFFFF;
  
  &:hover {
    background: #e63552;
    transform: translateY(-1px);
  }
`;

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    children
}) => {
    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContainer>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    <CloseButton onClick={onClose}>
                        <X />
                    </CloseButton>
                </ModalHeader>

                <ModalContent>
                    <ModalMessage>{message}</ModalMessage>

                    <ButtonGroup>
                        <CancelButton onClick={onClose}>
                            {cancelText}
                        </CancelButton>
                        <ConfirmButton onClick={handleConfirm}>
                            {confirmText}
                        </ConfirmButton>
                    </ButtonGroup>
                </ModalContent>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default ConfirmationModal;
